import { forwardRef, useCallback, useRef } from "react"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib"

interface CustomTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string
  error?: string
  onClearError?: () => void
  placeholder?: string
  rows?: number
}

export const CustomTextarea = forwardRef<HTMLTextAreaElement, CustomTextareaProps>(
  (
    { label, error, className = "", onClearError, required, placeholder, id, rows = 1, ...props },
    ref,
  ) => {
    const localRef = useRef<HTMLTextAreaElement>(null)

    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        localRef.current = node
        if (typeof ref === "function") {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref],
    )

    const adjustHeight = useCallback(() => {
      const textarea = localRef.current
      if (!textarea) return

      // Reset height to auto to allow shrinking
      textarea.style.height = "auto"
      // Set height to scrollHeight (capped at 200px)
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }, [])

    return (
      <div className="relative space-y-0 flex flex-col gap-1.25">
        <Label
          htmlFor={id}
          className={cn(
            "text-xs leading-5 uppercase",
            error ? "text-brand-terracotta-bright" : "text-body",
          )}
        >
          {label}
          {required && (
            <span className={error ? "text-brand-terracotta-bright" : "text-body"}>*</span>
          )}
        </Label>
        <Textarea
          ref={setRefs}
          id={id}
          rows={rows}
          {...props}
          onFocus={e => {
            const textarea = localRef.current
            if (textarea) {
              textarea.dataset.wasEmpty =
                !textarea.value || textarea.value.trim() === "" ? "true" : "false"
            }
            adjustHeight()
            props.onFocus?.(e)
          }}
          onBlur={e => {
            props.onBlur?.(e)
          }}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.preventDefault()
              const textarea = localRef.current
              if (!textarea) return

              const { selectionStart, value } = textarea

              // Check if we're at the start of the text (first line)
              const isFirstLine =
                selectionStart === 0 || (selectionStart === 1 && value.startsWith("• "))

              let newValue: string
              let cursorOffset: number

              if (isFirstLine && !value.startsWith("• ")) {
                // First line without bullet - add bullet prefix
                newValue = `${value.slice(0, selectionStart)}\n ${value.slice(selectionStart)}`
                cursorOffset = selectionStart + 4 // Position after "• " (original) + "• " (new line)
              } else {
                // Normal case: insert bullet at new line
                newValue = `${value.slice(0, selectionStart)}\n ${value.slice(selectionStart)}`
                cursorOffset = selectionStart + 3 // Position after "\n• "
              }

              // Set the new value and move cursor after the bullet
              textarea.value = newValue
              textarea.selectionStart = cursorOffset
              textarea.selectionEnd = cursorOffset
              // Trigger height adjustment
              adjustHeight()
              // Trigger onChange with the synthetic event
              const nativeEvent = new Event("change", { bubbles: true })
              props.onChange?.({
                ...e,
                target: textarea,
                nativeEvent,
              } as React.ChangeEvent<HTMLTextAreaElement>)
              // Clear error when user types
              if (error && onClearError) {
                onClearError()
              }
              return
            }
            props.onKeyDown?.(e)
          }}
          onChange={e => {
            const textarea = localRef.current
            let newValue = e.target.value

            // Only add bullet prefix when user starts typing from empty state
            const wasEmpty = textarea?.dataset.wasEmpty === "true"
            const isAtStart = e.target.selectionStart === 0

            if (
              textarea &&
              wasEmpty &&
              isAtStart &&
              newValue.length > 0 &&
              !newValue.startsWith("• ")
            ) {
              newValue = `• ${newValue}`
              textarea.value = newValue
              // Move cursor position to account for added bullet
              textarea.selectionStart = e.target.selectionStart + 2
              textarea.selectionEnd = e.target.selectionEnd + 2
              // Mark as no longer empty
              textarea.dataset.wasEmpty = "false"
            }

            adjustHeight()
            // Only clear error when user actually types something
            if (error && onClearError && newValue.length > 0) {
              onClearError()
            }
            props.onChange?.(e)
          }}
          className={cn(
            "border-0 border-b rounded-none shadow-none px-0 pb-2.5 pt-0 text-sm leading-5 font-semibold focus-visible:ring-0 resize-none placeholder:text-body !min-h-7.5 overflow-y-auto scrollbar-hide",
            error && "border-brand-terracotta-bright text-brand-terracotta-bright",
            className,
          )}
          placeholder={placeholder || "Type here ..."}
        />
        {error && <p className="text-xs text-brand-terracotta-bright">{error}</p>}
      </div>
    )
  },
)

CustomTextarea.displayName = "CustomTextarea"
